import React from 'react'
import { Flex, TextVariant, Text } from '@mprolab/uikit'
import styled, { css, keyframes, useTheme } from 'styled-components'
import { ClaimRewardActionType, ClaimStatus } from 'views/Rewards/types'

interface RewardStatusProps {
  status: ClaimStatus
  icon: React.ReactNode
  title: string
  actionType: ClaimRewardActionType
}

const animateBorder = () => keyframes`
  0% {
    opacity:1;
  }
  50% {
    opacity:0.8;
  }
  100% {
    opacity:1;
  }
`

const IconWrapper = styled(Flex)<{ statusColor: string; status }>`
  border-radius: 32px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.gray10};
  border: 1px solid ${({ statusColor }) => statusColor};
  ${({ status }) =>
    status === ClaimStatus.PENDING &&
    css`
      animation: ${animateBorder()} 1.5s infinite linear;
    `}
`

const RewardStatus: React.FC<RewardStatusProps> = ({ status, icon, title, actionType }) => {
  const { colors } = useTheme()
  const statusDescription = () => {
    switch (status) {
      case ClaimStatus.INITIAL:
      case ClaimStatus.ERROR:
        return `Not ${actionType === ClaimRewardActionType.UPDATE_REWARD ? 'minted' : 'claimed'}`
      case ClaimStatus.SUCCESS:
        return actionType === ClaimRewardActionType.UPDATE_REWARD ? 'Minted' : 'Claimed'
      case ClaimStatus.PENDING:
        return 'Pending'
      default:
        return ''
    }
  }

  const statusColor = () => {
    switch (status) {
      case ClaimStatus.INITIAL:
      case ClaimStatus.ERROR:
        return colors.red
      case ClaimStatus.SUCCESS:
        return colors.green
      case ClaimStatus.PENDING:
        return colors.yellow
      default:
        return null
    }
  }

  const statusColorBorder = () => {
    switch (status) {
      case ClaimStatus.INITIAL:
        return colors.gray10
      case ClaimStatus.SUCCESS:
        return colors.green
      case ClaimStatus.PENDING:
        return colors.purple
      default:
        return null
    }
  }

  return (
    <Flex gap="16px" alignItems="center">
      <IconWrapper statusColor={statusColorBorder()} {...{ status }}>
        {icon}
      </IconWrapper>
      <Flex flexDirection="column">
        <Text variant={TextVariant.BODY_SMALL_BOLD}>{title}</Text>
        <Flex gap="4px">
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
            Status:
          </Text>
          <Text variant={TextVariant.TEXT_XS_REGULAR} color={statusColor()}>
            {statusDescription()}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default RewardStatus
